import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, FileText, Loader2, Sparkles, Upload, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  downloadDocument,
  downloadSignedDocument,
  generateDocument,
  getTemplate,
  uploadDocument
} from '../../services/api/implementation/impl';

const DealAgreementDocsSection = ({ dealId, projectId, generatedDocs = [], onUpdated }) => {
  const [generatingId, setGeneratingId] = useState(null);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['projectTemplates', projectId],
    queryFn: () => getTemplate(projectId),
    enabled: !!projectId
  });

  const generateMutation = useMutation({
    mutationFn: (templateId) =>
      generateDocument({
        id: templateId,
        deal_id: dealId,
        date: new Date().toISOString().split('T')[0]
      }),
    onSuccess: () => {
      toast.success('Agreement generated — download draft, then upload signed copy');
      onUpdated?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Generate failed');
    },
    onSettled: () => setGeneratingId(null)
  });

  const downloadMutation = useMutation({
    mutationFn: (docId) => downloadDocument(docId),
    onSuccess: (data, docId) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `agreement_draft_${docId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Draft downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Download failed');
    }
  });

  const downloadSignedMutation = useMutation({
    mutationFn: (docId) => downloadSignedDocument(docId),
    onSuccess: (data, docId) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed_agreement_${docId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Signed copy downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Download failed');
    }
  });

  const uploadSignedMutation = useMutation({
    mutationFn: async ({ docId, file }) => {
      setUploadingDocId(docId);
      const formData = new FormData();
      formData.append('files', file);
      return uploadDocument(docId, formData);
    },
    onSuccess: () => {
      toast.success('Signed document uploaded');
      onUpdated?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
    },
    onSettled: () => setUploadingDocId(null)
  });

  const isAiTemplate = (tpl) =>
    (tpl?.description || '').includes('Promoted from doc template definition');

  const docByTemplateName = useMemo(() => {
    const map = new Map();
    for (const doc of generatedDocs || []) {
      const key = (doc.document_name || '').toLowerCase();
      if (!map.has(key)) map.set(key, doc);
    }
    return map;
  }, [generatedDocs]);

  const triggerSignedUpload = (docId) => {
    uploadTargetRef.current = docId;
    fileInputRef.current?.click();
  };

  const onSignedFilePicked = (e) => {
    const file = e.target.files?.[0];
    const docId = uploadTargetRef.current;
    e.target.value = '';
    if (!file || !docId) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF');
      return;
    }
    uploadSignedMutation.mutate({ docId, file });
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
        <h3 className="text-sm font-semibold text-gray-900">Agreement documents</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          Generate from templates, download the draft, then upload the signed PDF. Names come
          from Applicants above.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={onSignedFilePicked}
      />

      <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5">
        <div className="min-w-0">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Available templates
          </p>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading templates…</p>
          ) : !templates?.length ? (
            <p className="text-sm text-gray-500">No templates for this project yet.</p>
          ) : (
            <ul className="space-y-2">
              {templates.map((tpl) => {
                const ai = isAiTemplate(tpl);
                const existing = docByTemplateName.get((tpl.document_name || '').toLowerCase());
                return (
                  <li
                    key={tpl.id}
                    className="rounded-xl border border-gray-200 bg-gray-50/50 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                          {tpl.document_name}
                          {ai ? (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-teal-800 ring-1 ring-teal-200">
                              <Sparkles className="h-2.5 w-2.5" />
                              AI
                            </span>
                          ) : null}
                        </p>
                        {existing ? (
                          <p className="mt-1 text-xs text-emerald-700">
                            Generated ·{' '}
                            {existing.is_uploaded
                              ? 'signed copy on file'
                              : 'awaiting signed upload'}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        disabled={generateMutation.isPending}
                        onClick={() => {
                          setGeneratingId(tpl.id);
                          generateMutation.mutate(tpl.id);
                        }}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#2A565E]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#2A565E] hover:bg-teal-50 disabled:opacity-50">
                        {generatingId === tpl.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="h-3.5 w-3.5" />
                        )}
                        {existing ? 'Regenerate' : 'Generate'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="min-w-0">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Generated for this deal
          </p>
          {!generatedDocs?.length ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-8 text-center">
              <FileText className="mx-auto h-7 w-7 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">No agreements generated yet</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {generatedDocs.map((doc) => (
                <li key={doc.id} className="rounded-xl border border-gray-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {doc.document_name || `Document #${doc.id}`}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {doc.is_uploaded ? (
                          <span className="font-medium text-emerald-700">Signed uploaded</span>
                        ) : (
                          <span className="text-amber-700">Draft only — upload signed PDF</span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => downloadMutation.mutate(doc.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        <Download className="h-3.5 w-3.5" />
                        Draft
                      </button>
                      {doc.is_uploaded ? (
                        <button
                          type="button"
                          onClick={() => downloadSignedMutation.mutate(doc.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100">
                          <Download className="h-3.5 w-3.5" />
                          Signed
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={uploadingDocId === doc.id}
                          onClick={() => triggerSignedUpload(doc.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-[#2A565E]/30 bg-white px-2.5 py-1.5 text-xs font-semibold text-[#2A565E] hover:bg-teal-50 disabled:opacity-50">
                          {uploadingDocId === doc.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Upload className="h-3.5 w-3.5" />
                          )}
                          Upload signed
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default DealAgreementDocsSection;
